import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

export interface GpsPosition {
  lat: number;
  lon: number;
  heading: number | null;
  accuracy: number;
  timestamp: number;
}

export interface UseGpsTrackingOptions {
  enableHighAccuracy?: boolean;
  maximumAge?: number;
  timeout?: number;
}

export interface UseGpsTrackingResult {
  position: GpsPosition | null;
  error: GeolocationPositionError | null;
  isTracking: boolean;
  isSupported: boolean;
  start: () => void;
  stop: () => void;
}

const DEFAULT_OPTIONS: UseGpsTrackingOptions = {
  enableHighAccuracy: true,
  maximumAge: 0,
  timeout: 10000,
};

export function useGpsTracking(
  options: UseGpsTrackingOptions = {}
): UseGpsTrackingResult {
  const [position, setPosition] = useState<GpsPosition | null>(null);
  const [error, setError] = useState<GeolocationPositionError | null>(null);
  const [isTracking, setIsTracking] = useState(false);
  const watchIdRef = useRef<number | null>(null);

  const isSupported = typeof navigator !== 'undefined' && 'geolocation' in navigator;

  // Memoize options to prevent unnecessary re-renders
  const mergedOptions = useMemo(
    () => ({
      enableHighAccuracy: options.enableHighAccuracy ?? DEFAULT_OPTIONS.enableHighAccuracy,
      maximumAge: options.maximumAge ?? DEFAULT_OPTIONS.maximumAge,
      timeout: options.timeout ?? DEFAULT_OPTIONS.timeout,
    }),
    [options.enableHighAccuracy, options.maximumAge, options.timeout]
  );

  // Store options in ref to avoid dependency issues
  const optionsRef = useRef(mergedOptions);
  optionsRef.current = mergedOptions;

  const handleSuccess = useCallback((pos: GeolocationPosition) => {
    setPosition({
      lat: pos.coords.latitude,
      lon: pos.coords.longitude,
      heading: pos.coords.heading,
      accuracy: pos.coords.accuracy,
      timestamp: pos.timestamp,
    });
    setError(null);
  }, []);

  const handleError = useCallback((err: GeolocationPositionError) => {
    setError(err);
    setIsTracking(false);
    console.error('GPS Error:', err.message, 'Code:', err.code);

    // Debug info for iOS Safari issues
    if (err.code === 1) {
      console.error('PERMISSION_DENIED. Check: iOS Settings → Privacy → Location Services → Safari');
    }
  }, []);

  const start = useCallback(() => {
    if (!isSupported) {
      console.error('Geolocation is not supported');
      return;
    }

    if (watchIdRef.current !== null) {
      // Already tracking
      return;
    }

    setIsTracking(true);
    setError(null);

    const geoOptions = {
      enableHighAccuracy: optionsRef.current.enableHighAccuracy,
      maximumAge: optionsRef.current.maximumAge,
      timeout: optionsRef.current.timeout,
    };

    // Check permission state if available (not on Safari, but helps debug)
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        console.log('Geolocation permission state:', result.state);
      }).catch(() => {
        console.log('Permissions API not available for geolocation');
      });
    }

    // First call getCurrentPosition to trigger permission dialog (Safari requirement)
    // Then start watchPosition for continuous tracking
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        handleSuccess(pos);
        // Now start watching
        watchIdRef.current = navigator.geolocation.watchPosition(
          handleSuccess,
          handleError,
          geoOptions
        );
      },
      handleError,
      geoOptions
    );
  }, [isSupported, handleSuccess, handleError]);

  const stop = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setIsTracking(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, []);

  return {
    position,
    error,
    isTracking,
    isSupported,
    start,
    stop,
  };
}
